import { DecimalPipe } from '@angular/common';
import {
  AfterViewInit,
  Component,
  ElementRef,
  NgZone,
  OnInit,
  computed,
  effect,
  inject,
  output,
  signal,
  viewChild,
  ChangeDetectionStrategy
} from '@angular/core';
import { Router, RouterLink } from '@angular/router';
import { AuthService } from '../../core/services/auth.service';
import { CreditUsageStore } from '../../core/services/credit-usage.store';
import { TabService } from '../../core/services/tab.service';

export interface NotificationItem {
  id: string;
  type: 'mention' | 'ticket' | 'alert' | 'system';
  title: string;
  message: string;
  time: string;
  read: boolean;
}

@Component({
  selector: 'app-tab-bar',
  standalone: true,
  imports: [RouterLink, DecimalPipe],
  changeDetection: ChangeDetectionStrategy.Eager,
  templateUrl: './tab-bar.html',
})
export class TabBarComponent implements AfterViewInit, OnInit {
  readonly tabService = inject(TabService);
  private authService = inject(AuthService);
  private router = inject(Router);
  private ngZone = inject(NgZone);
  readonly creditStore = inject(CreditUsageStore);

  /** Emitted when the hamburger button is clicked (mobile only, shown via CSS) */
  readonly toggleDrawer = output<void>();

  readonly notifOpen = signal(false);
  readonly profileOpen = signal(false);

  // Right-click context menu state
  readonly contextMenu = signal<{ tabId: string; x: number; y: number } | null>(null);

  // Tab scroll state
  readonly tabsScrollEl = viewChild<ElementRef<HTMLElement>>('tabsScroll');
  readonly canScrollLeft = signal(false);
  readonly canScrollRight = signal(false);

  // Index of the active tab — unused now but kept for future use
  readonly activeTabIndex = computed(() =>
    this.tabService.tabs().findIndex((t) => t.id === this.tabService.activeTabId()),
  );

  constructor() {
    // Re-check scroll state whenever tabs list changes (runs after render)
    effect(() => {
      this.tabService.tabs();
      // Defer to let the DOM update first
      setTimeout(() => this.checkScrollState(), 50);
    });
  }

  ngOnInit(): void {
    this.creditStore.ensureLoaded();
  }

  ngAfterViewInit(): void {
    const el = this.tabsScrollEl()?.nativeElement;
    if (!el) return;
    this.checkScrollState();
    // ResizeObserver runs outside zone — run update inside zone so signals trigger CD
    new ResizeObserver(() => {
      this.ngZone.run(() => this.checkScrollState());
    }).observe(el);
  }

  /** Called from (scroll) binding in template — already inside Angular zone */
  onTabScroll(event: Event): void {
    this.updateScrollState(event.target as HTMLElement);
  }

  checkScrollState(): void {
    const el = this.tabsScrollEl()?.nativeElement;
    if (el) this.updateScrollState(el);
  }

  updateScrollState(el: HTMLElement): void {
    this.canScrollLeft.set(el.scrollLeft > 2);
    this.canScrollRight.set(el.scrollLeft < el.scrollWidth - el.clientWidth - 2);
  }

  scrollTabsBy(delta: number): void {
    const el = this.tabsScrollEl()?.nativeElement;
    if (el) el.scrollBy({ left: delta, behavior: 'smooth' });
  }

  scrollActiveTabIntoView(): void {
    setTimeout(() => {
      const el = this.tabsScrollEl()?.nativeElement;
      if (!el) return;
      const activeEl = el.querySelector('[data-active="true"]') as HTMLElement | null;
      activeEl?.scrollIntoView({ block: 'nearest', inline: 'nearest' });
      this.checkScrollState();
    }, 60);
  }

  readonly notifications = signal<NotificationItem[]>([
    {
      id: 'n1',
      type: 'mention',
      read: false,
      time: '2m ago',
      title: 'New Brand Mention',
      message: '@locobuzz trending on Twitter with 1.2K mentions in last hour',
    },
    {
      id: 'n2',
      type: 'ticket',
      read: false,
      time: '8m ago',
      title: 'Urgent Ticket Assigned',
      message: 'Ticket #83481 marked urgent — GMB Review by Andreu Mole',
    },
    {
      id: 'n3',
      type: 'alert',
      read: false,
      time: '25m ago',
      title: 'Sentiment Alert',
      message: 'Negative sentiment spike detected on Facebook — 34% drop',
    },
    {
      id: 'n4',
      type: 'system',
      read: true,
      time: '1h ago',
      title: 'Report Ready',
      message: 'Your Earned Insights Monthly report is ready to download',
    },
    {
      id: 'n5',
      type: 'ticket',
      read: true,
      time: '3h ago',
      title: 'Ticket Resolved',
      message: 'Ticket #82940 has been closed by Agent Priya Sharma',
    },
  ]);

  readonly unreadCount = signal(3);

  markAllRead(): void {
    this.notifications.update((list) => list.map((n) => ({ ...n, read: true })));
    this.unreadCount.set(0);
  }

  markRead(id: string): void {
    this.notifications.update((list) => list.map((n) => (n.id === id ? { ...n, read: true } : n)));
    this.unreadCount.update((c) => Math.max(0, c - 1));
  }

  activate(id: string, route: string): void {
    this.tabService.activateTab(id);
    this.router.navigate([route]);
    this.scrollActiveTabIntoView();
  }

  close(id: string, event: MouseEvent): void {
    event.stopPropagation();
    const nextRoute = this.tabService.closeTab(id);
    if (nextRoute) this.router.navigate([nextRoute]);
    else this.router.navigate(['/api-key-integration']);
  }

  onTabRightClick(tabId: string, event: MouseEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.contextMenu.set({ tabId, x: event.clientX, y: event.clientY });
    this.notifOpen.set(false);
    this.profileOpen.set(false);
  }

  dismissContextMenu(): void {
    this.contextMenu.set(null);
  }

  onProfileBackdropKeydown(event: KeyboardEvent): void {
    if (event.key === 'Escape') {
      this.profileOpen.set(false);
    }
  }

  onContextMenuBackdropKeydown(event: KeyboardEvent): void {
    if (event.key === 'Escape') {
      this.dismissContextMenu();
    }
  }

  ctxClose(tabId: string): void {
    this.dismissContextMenu();
    const nextRoute = this.tabService.closeTab(tabId);
    if (nextRoute) this.router.navigate([nextRoute]);
    else this.router.navigate(['/api-key-integration']);
  }

  ctxCloseOthers(tabId: string): void {
    this.dismissContextMenu();
    const route = this.tabService.closeOtherTabs(tabId);
    const tab = this.tabService.tabs().find((t) => t.id === tabId);
    if (tab) this.router.navigate([tab.route]);
    else if (route) this.router.navigate([route]);
  }

  ctxCloseToRight(tabId: string): void {
    this.dismissContextMenu();
    const route = this.tabService.closeTabsToRight(tabId);
    if (route) this.router.navigate([route]);
  }

  ctxCloseAll(): void {
    this.dismissContextMenu();
    this.tabService.closeAllTabs();
    this.router.navigate(['/api-key-integration']);
  }

  navigateTo(route: string, id: string, title: string): void {
    this.tabService.openTab({ id, title, route });
    this.router.navigate([route]);
  }

  logout(): void {
    this.authService.logout();
  }

  userDisplayName(): string {
    return this.authService.displayName();
  }

  remainingCreditsLabel(): string {
    const remaining = this.creditStore.remainingCredits();
    if (remaining === null) return 'Remaining credits';
    return `Remaining credits: ${remaining.toLocaleString()}`;
  }

  userInitials(): string {
    return this.authService.displayInitials();
  }

  userSubtitle(): string {
    const p = this.authService.apiProfile();
    const u = this.authService.currentUser();
    const roleRaw = p?.user_type?.trim() || u?.role || 'Member';
    const role = roleRaw.replace(/_/g, ' ');
    const email = p?.email?.trim() || u?.email || '';
    return email ? `${role} · ${email}` : role;
  }

  readonly profileMenuItems: Array<{
    label: string;
    icon: string;
    divider?: boolean;
    danger?: boolean;
    action: () => void;
  }> = [
    {
      label: 'My Profile',
      icon: 'M16 7a4 4 0 1 1-8 0 4 4 0 0 1 8 0zM12 14a7 7 0 0 0-7 7h14a7 7 0 0 0-7-7z',
      action: () => this.navigateTo('/profile', 'profile', 'My Profile'),
    },
    {
      label: 'Settings',
      icon: 'M9.594 3.94c.09-.542.56-.94 1.11-.94h2.593c.55 0 1.02.398 1.11.94l.213 1.281c.063.374.313.686.645.87.074.04.147.083.22.127.324.196.72.257 1.075.124l1.217-.456a1.125 1.125 0 011.37.49l1.296 2.247a1.125 1.125 0 01-.26 1.431l-1.003.827c-.293.24-.438.613-.431.992a6.759 6.759 0 010 .255c-.007.378.138.75.43.99l1.005.828c.424.35.534.954.26 1.43l-1.298 2.247a1.125 1.125 0 01-1.369.491l-1.217-.456c-.355-.133-.75-.072-1.076.124a6.57 6.57 0 01-.22.128c-.331.183-.581.495-.644.869l-.213 1.28c-.09.543-.56.941-1.11.941h-2.594c-.55 0-1.02-.398-1.11-.94l-.213-1.281c-.062-.374-.312-.686-.644-.87a6.52 6.52 0 01-.22-.127c-.325-.196-.72-.257-1.076-.124l-1.217.456a1.125 1.125 0 01-1.369-.49l-1.297-2.247a1.125 1.125 0 01.26-1.431l1.004-.827c.292-.24.437-.613.43-.992a6.932 6.932 0 010-.255c.007-.378-.138-.75-.43-.99l-1.004-.828a1.125 1.125 0 01-.26-1.43l1.297-2.247a1.125 1.125 0 011.37-.491l1.216.456c.356.133.751.072 1.076-.124.072-.044.146-.087.22-.128.332-.183.582-.495.644-.869l.213-1.281z M15 12a3 3 0 11-6 0 3 3 0 016 0z',
      action: () => this.navigateTo('/settings', 'settings', 'Settings'),
    },
    { label: '', icon: '', divider: true, action: () => {} },
    {
      label: 'Sign Out',
      icon: 'M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 0 1-3 3H6a3 3 0 0 1-3-3V7a3 3 0 0 1 3-3h4a3 3 0 0 1 3 3v1',
      danger: true,
      action: () => this.logout(),
    },
  ];

  notifIconBg(type: string): string {
    const map: Record<string, string> = {
      mention: 'rgba(99,102,241,0.12)',
      ticket: 'rgba(59,125,216,0.12)',
      alert: 'rgba(245,158,11,0.12)',
      system: 'rgba(16,185,129,0.12)',
    };
    return map[type] ?? 'rgba(148,163,184,0.12)';
  }

  notifIconColor(type: string): string {
    const map: Record<string, string> = {
      mention: '#6366f1',
      ticket: '#3b7dd8',
      alert: '#f59e0b',
      system: '#10b981',
    };
    return map[type] ?? '#94a3b8';
  }

  notifIconPath(type: string): string {
    const map: Record<string, string> = {
      mention:
        'M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2h-3l-4 4z',
      ticket:
        'M15 5v2m0 4v2m0 4v2M5 5a2 2 0 0 0-2 2v3a2 2 0 1 1 0 4v3a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-3a2 2 0 1 1 0-4V7a2 2 0 0 0-2-2H5z',
      alert:
        'M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z',
      system: 'M9 12l2 2 4-4m6 2a9 9 0 1 1-18 0 9 9 0 0 1 18 0z',
    };
    return map[type] ?? 'M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0z';
  }
}
